import { useEffect, useState } from 'react';
import {
  Box, ButtonBase, Skeleton, Typography, useTheme,
} from '@mui/material';
import { yellow } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FALLBACK_IMAGE_URL } from '@optimus/utils/src/constant';
import OptimusButton from '../../../common/library/Button/Button';
import { TestIds } from '../../../assets/testIds';
import { getContentByCategory } from '../../../api/contentAPI';
import { Content } from '../../../common/models/content/Content';
import { useDisplayContentView } from '../../../common/hooks/useDisplayContentView';
import { getHeroFormat, getImageUrl } from '../Content/ContentCommonFunctions';
import { RESOURCES_ROUTE } from '../../../routes/namedRoutes';
import { useLockedContentAction } from '../Content/useLockedContentAction';
import { ContentContactSalesDialog } from '../Content/ContentContactSalesDialog';
import { Logger } from '../../../config/logging/logger';

const logger = Logger.create('HomeHighlights');

// The content topic behind the Resources page's Featured filter.
const FEATURED_TOPIC = 'Featured';
const HIGHLIGHT_COUNT = 2;

// Content types arrive as e.g. "downloadable_resource"; the card shows "Downloadable Resource".
const formatContentType = (type: string) => type
  .split(/[\s_-]+/)
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

/**
 * One featured piece. Locked content keeps its card - title and image stay - but a click
 * runs the same upgrade / contact-sales flow as the Resources feed instead of opening it.
 */
function HighlightCard({ content }: { content: Content }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const displayContentView = useDisplayContentView();
  const { attributes } = content;
  const isLocked = !!attributes?.locked;
  const {
    requiresSalesContact,
    hasContactedSales,
    handleLockedClick,
    contactSalesDialogProps,
  } = useLockedContentAction({
    contentId: content.id,
    contentTitle: attributes?.title,
    type: attributes?.type,
    metadata: attributes?.metadata,
    lowestMembershipRequired: attributes?.lowestMembershipRequired,
  });

  const lockedLabel = (() => {
    if (requiresSalesContact && hasContactedSales) {
      return {
        key: 'content.contactSales.confirmation',
        testId: TestIds.contentFeed.contactSalesConfirmationMsg,
      };
    }
    return requiresSalesContact ?
      { key: 'content.contactSales.cta', testId: TestIds.contentFeed.contactSalesBtn } :
      { key: 'content.upgradeToUnlock', testId: TestIds.contentFeed.upgradeBtn };
  })();

  return (
    <>
      <ButtonBase
        data-testid={TestIds.homePage.highlightCard}
        onClick={(event) => (isLocked ? handleLockedClick(event) : displayContentView(content))}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          // ButtonBase centers its content by default; the card reads left to right.
          justifyContent: 'flex-start',
          gap: theme.spacing(2),
          width: '100%',
          padding: theme.spacing(2),
          backgroundColor: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: theme.spacing(2),
          textAlign: 'left',
        }}
      >
        <Box sx={{
          position: 'relative',
          width: 140,
          height: 100,
          flexShrink: 0,
          borderRadius: theme.spacing(1),
          overflow: 'hidden',
          [theme.breakpoints.down('sm')]: { width: 96, height: 72 },
        }}>
          <Box
            component="img"
            alt=""
            src={getImageUrl(getHeroFormat(content)?.url) || FALLBACK_IMAGE_URL}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              // Same treatment as a locked card in the Resources feed.
              ...(isLocked && { filter: 'blur(4px)' }),
            }}
          />
          {isLocked && (
            <Box
              data-testid={TestIds.contentFeed.lockedOverlay}
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(yellow[800], 0.4),
              }}
            >
              <LockIcon sx={{ color: theme.palette.common.white }} />
            </Box>
          )}
        </Box>
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: theme.spacing(1), minWidth: 0,
        }}>
          {attributes?.type && (
            <Typography sx={{
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: '0.1px',
              color: theme.palette.brand.primary,
            }}>
              {formatContentType(attributes.type)}
            </Typography>
          )}
          <Typography sx={{
            fontSize: 18,
            fontWeight: 500,
            lineHeight: 1.6,
            letterSpacing: '0.15px',
            color: theme.palette.common.black,
          }}>
            {attributes?.title}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {attributes?.short_description}
          </Typography>
          {isLocked && (
            <Box
              data-testid={lockedLabel.testId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
                backgroundColor: yellow[600],
                borderRadius: theme.spacing(0.75),
              }}
            >
              <LockIcon sx={{ fontSize: theme.spacing(2) }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: theme.spacing(1.5) }}>
                {t(lockedLabel.key)}
              </Typography>
            </Box>
          )}
        </Box>
      </ButtonBase>
      <ContentContactSalesDialog {...contactSalesDialogProps} />
    </>
  );
}

/**
 * Two featured content pieces for the home page (SN-1046). The content API orders
 * results randomly when no sort is given, so the pair changes on every visit.
 */
export function HomeHighlights() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [highlights, setHighlights] = useState<Content[] | null>(null);

  useEffect(() => {
    let isCurrent = true;
    getContentByCategory({ contentTopics: FEATURED_TOPIC, page: 1, pageSize: HIGHLIGHT_COUNT })
      .then((contentList: Content[]) => {
        if (!isCurrent) return;
        setHighlights(contentList.map((content, index) => ({
          ...content,
          contentFeedSection: { index: 0, name: 'home: highlights' },
          contentFeedSectionPosition: index + 1,
        })));
      })
      .catch((error) => {
        logger.error(Logger.format('Failed to load home highlights', (error as Error)?.message));
        if (isCurrent) setHighlights([]);
      });
    return () => {
      isCurrent = false;
    };
  }, []);

  // ContentHome matches ?filter= against the tab's translated tag, not the API topic.
  const featuredFilterTag = encodeURIComponent(`${t('home.contentFilters.featured.tag')}`);
  const viewMoreUrl = `${RESOURCES_ROUTE}?filter=${featuredFilterTag}`;

  // Nothing featured to show: leave the section out rather than show an empty box.
  if (highlights?.length === 0) return null;

  return (
    <Box
      data-testid={TestIds.homePage.highlights}
      sx={{
        display: 'flex', flexDirection: 'column', gap: theme.spacing(2.5), paddingY: theme.spacing(2),
      }}
    >
      <Typography sx={{ fontSize: 24, lineHeight: 1.334, color: theme.palette.text.primary }}>
        {t('homePage.highlights.heading')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2.5) }}>
        {highlights ?
          highlights.map((content) => <HighlightCard key={content.id} content={content} />) :
          Array.from({ length: HIGHLIGHT_COUNT }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={134} sx={{ borderRadius: theme.spacing(2) }} />
          ))}
        <OptimusButton
          customVariant="secondaryGray"
          size="s"
          data-testid={TestIds.homePage.viewMoreBtn}
          buttonText={`${t('homePage.highlights.viewMore')}`}
          trailingIcon={ArrowForwardIcon}
          onClick={() => navigate(viewMoreUrl)}
          sx={{
            marginTop: theme.spacing(-1.25),
            borderColor: theme.palette.grey[200],
            boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
          }}
        />
      </Box>
    </Box>
  );
}
